/* eslint-disable max-statements */

import { Alert, Box, Button, Checkbox, Divider, Grid, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { TextInput } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useGenerateIntervatReport, useGenerateIntracomReport, useGenerateIntrastatReport } from '@hooks/declaration';
import { useAcceptVatReportDisclaimer, useUser } from '@hooks/user';
import { useVatReportFormInitializer, VatReportFormValues } from './useVatReportFormInitializer';
import { VatReport } from './VatReport';
import { VATReportDisclaimer } from './VatReportDisclaimer';

export const VATReports = () => {
  const generateIntervatReportMutation = useGenerateIntervatReport();
  const generateIntrastatReportMutation = useGenerateIntrastatReport();
  const generateIntracomReportMutation = useGenerateIntracomReport();

  const { data: declarant, isLoading } = useDeclarant();
  const { data: user } = useUser();
  const updateDeclarantMutation = useUpdateDeclarant();
  const acceptDisclaimerMutation = useAcceptVatReportDisclaimer();
  const { form, buildFormValues } = useVatReportFormInitializer({ declarant });

  const [pendingReportCriteria, setPendingReportCriteria] = useState<{
    year: number;
    month: number | null;
    quarter: number | null;
    type: 'intrastat' | 'intervat' | 'intracom';
  } | null>(null);

  const handleSubmit = (values: VatReportFormValues) => {
    if (declarant) {
      updateDeclarantMutation.mutate({
        declarantId: declarant._id,
        declarant: { ...values },
      });
    }
  };

  useEffect(() => {
    form.setValues(buildFormValues(declarant));
    form.resetDirty({});
  }, [declarant]);

  const openVatReportDisclaimer = (options: { onAccept?: () => void; isLoading?: boolean; readOnly?: boolean }) =>
    modals.open({
      title: 'Legal Disclaimer: Preparation Reports',
      size: 'xl',
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: <VATReportDisclaimer onAccept={options.onAccept} isLoading={options.isLoading} readOnly={options.readOnly} />,
    });

  const createReportMutationWrapper = (
    type: 'intrastat' | 'intervat' | 'intracom',
    originalMutation: { mutate: (criteria: { year: number; month: number | null; quarter: number | null }) => void },
  ) => ({
    mutate: (criteria: { year: number; month: number | null; quarter: number | null }) => {
      if (!user?.vatReportDisclaimerAccepted) {
        setPendingReportCriteria({ ...criteria, type });
        openVatReportDisclaimer({
          onAccept: handleAcceptDisclaimer,
          isLoading: acceptDisclaimerMutation.isPending,
          readOnly: false,
        });
      } else {
        originalMutation.mutate(criteria);
      }
    },
  });

  const handleAcceptDisclaimer = () => {
    acceptDisclaimerMutation.mutate(undefined, {
      onSuccess: () => {
        if (pendingReportCriteria) {
          const { type, ...criteria } = pendingReportCriteria;
          switch (type) {
            case 'intrastat':
              generateIntrastatReportMutation.mutate(criteria);
              break;
            case 'intervat':
              generateIntervatReportMutation.mutate(criteria);
              break;
            case 'intracom':
              generateIntracomReportMutation.mutate(criteria);
              break;
          }
          setPendingReportCriteria(null);
        }
      },
    });
  };

  const handleOpenDisclaimerReadOnly = () => {
    openVatReportDisclaimer({
      readOnly: true,
    });
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack>
      {user?.vatReportDisclaimerAccepted && (
        <Alert variant="light" color="yellow" title="VAT Reports Disclaimer" icon={<IconAlertTriangle size={20} />}>
          <Text size="sm">
            Important information about VAT report preparation.
            <Button
              variant="transparent"
              p={0}
              h="auto"
              c="yellow.7"
              fw={600}
              td="underline"
              onClick={handleOpenDisclaimerReadOnly}
              data-pw="view-vat-disclaimer-link"
              styles={{
                root: {
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  minWidth: 'auto',
                  minHeight: 'auto',
                },
              }}
            >
              Read more
            </Button>
          </Text>
        </Alert>
      )}

      <Grid>
        <Grid.Col span={3}>
          <VatReport title="Intrastat report" downloadReport={createReportMutationWrapper('intrastat', generateIntrastatReportMutation)} />
        </Grid.Col>
        <Grid.Col span={3} offset={1}>
          <VatReport title="Intervat report" downloadReport={createReportMutationWrapper('intervat', generateIntervatReportMutation)} />
        </Grid.Col>
        <Grid.Col span={3} offset={1}>
          <VatReport title="Intracom report" downloadReport={createReportMutationWrapper('intracom', generateIntracomReportMutation)} />
        </Grid.Col>
      </Grid>

      <Divider mt="md" />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl" mt="lg">
          <Box>
            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('vatReport.name')} label="Name" data-pw="declarant-vat-report-name-input" />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('vatReport.identificationNumber')} label="VAT number" data-pw="declarant-vat-report-id-input" />
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Grid>
              <Grid.Col span={8}>
                <Checkbox
                  mb={4}
                  {...form.getInputProps('vatReport.excludeProcedure40')}
                  label="Exclude Procedure 40"
                  checked={form.values.vatReport.excludeProcedure40}
                />
                <Text c="dimmed" size="sm" fs="italic">
                  Exclude declarations with current procedure 40 from the VAT report
                </Text>
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Grid>
              <Grid.Col span={8}>
                <Checkbox
                  mb={4}
                  {...form.getInputProps('vatReport.authorizedET14000')}
                  label="ET14000 Authorization active (Postponed accounting)"
                  checked={form.values.vatReport.authorizedET14000}
                  data-pw="declarant-vat-report-et14000-checkbox"
                />
                <Text c="dimmed" size="sm" fs="italic">
                  Please confirm if the VAT number holder has a valid ET14000 authorization for this period. This selection determines VAT entry in boxes 57 and
                  87.
                </Text>
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Title order={4} fw={600} mb="md">
              Address
            </Title>
            <Grid mt="md">
              <Grid.Col span={6}>
                <TextInput {...form.getInputProps('vatReport.address.street')} label="Address" data-pw="declarant-vat-report-address-input" />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput {...form.getInputProps('vatReport.address.postcode')} label="Postcode" data-pw="declarant-vat-report-postcode-input" />
              </Grid.Col>
            </Grid>

            <Grid mt="md">
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vatReport.address.city')} label="City" data-pw="declarant-vat-report-city-input" />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vatReport.address.country')} label="Country" data-pw="declarant-vat-report-country-input" />
              </Grid.Col>
              <Grid.Col span={3}>
                <TextInput {...form.getInputProps('vatReport.address.region')} label="Region" data-pw="declarant-vat-report-region-input" />
              </Grid.Col>
            </Grid>
          </Box>

          <Box>
            <Title order={4} fw={600} mb="md">
              Contact person
            </Title>
            <Grid>
              <Grid.Col span={4}>
                <TextInput {...form.getInputProps('vatReport.contactPerson.email')} label="E-mail" data-pw="contact-email-input" />
              </Grid.Col>
            </Grid>
          </Box>

          <Group py="xl">
            <Button
              type="submit"
              variant="filled"
              size="lg"
              loading={updateDeclarantMutation.isPending}
              disabled={!form.isValid()}
              data-pw="save-declarant-vat-report-button"
            >
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Stack>
  );
};
